import { PermissionsChangedEnum } from './permissions-changed.enum';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Signal, computed, inject } from '@angular/core';

import { BehaviorSubject, Observable, of, race, throwError, timer } from 'rxjs';
import {
  map,
  distinctUntilChanged,
  filter,
  take,
  switchMap,
  catchError,
} from 'rxjs/operators';
import { decodeJwt } from '@ups/xplat/utils';

import {
  AuthenticatedIdentity,
  AuthenticationService,
  identityKey,
} from './authentication.service';

// eslint-disable-next-line @nx/enforce-module-boundaries -- platform HTTP/logging wiring
import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  handleReturnObject,
  LogService,
  EventBusService,
  environment,
  LocalStorageService,
  normalizeEnvironmentName,
} from '@ups/xplat/core';

import { SecurityFeatureGroupModel } from './security-feature-group-model';
import { SecurityFeatureModel } from './security-feature-model';
import { SecurityProfile } from './security-profile.model';
import { unwrapSecurityProfile } from './security-profile.util';
import { Store } from '@ngrx/store';
import { PermissionType } from '@ups/security-constants';
import { toSignal } from '@angular/core/rxjs-interop';
import { isIOS } from '@ups/xplat/utils';
import { Auth0AuthenticationService } from './auth0-authentication.service';

export interface ISecurityFeatureGroupsIndexer {
  [index: string]: SecurityFeatureGroupModel;
}

export interface ISecurityFeaturesIndexer {
  [index: string]: SecurityFeatureModel;
}

interface CachedPermissions {
  data: Array<unknown>;
  timestamp: number;
  version: string;
}

@Injectable({
  providedIn: 'root',
})
export class SecurityService {
  log = inject(LogService);
  eventBus = inject(EventBusService);
  store = inject(Store);
  auth = inject(AuthenticationService);
  storage = inject(LocalStorageService);
  clientFactory = inject(MyHttpClientFactory);

  public featureGroupsByName: ISecurityFeatureGroupsIndexer = {};
  public featuresById: ISecurityFeaturesIndexer = {};
  public featuresByUniqName: ISecurityFeaturesIndexer = {};
  public permissionsChanged$ = new BehaviorSubject<PermissionsChangedEnum>(
    PermissionsChangedEnum.Uninitialized
  );
  protected httpClient: HttpClient;

  private permissionChanged = toSignal(this.permissionsChanged$);
  private lastIdentityKey: string | null = null;

  constructor() {
    this.httpClient = this.clientFactory.createHttpClient(
      environment.security.apiUrl,
      true,
      ResponseCasingEnum.CamelCase
    );

    this.auth.identity$
      .pipe(
        distinctUntilChanged((a, b) => identityKey(a) === identityKey(b)),
        filter(
          (identity): identity is AuthenticatedIdentity => identity !== null
        )
      )
      .subscribe((identity) => {
        this.loadPermissionsForIdentity(identity, 'identitySource');
      });

    if (this.auth instanceof Auth0AuthenticationService) {
      this.eventBus
        .observe(this.eventBus.types.profileUpdated)
        .pipe(
          map((info: { Auth0UserId?: string } | null) => info?.Auth0UserId),
          filter((auth0UserId) => !!auth0UserId),
          distinctUntilChanged()
        )
        .subscribe((auth0UserId) => {
          if (!auth0UserId) {
            return;
          }
          this.loadPermissionsForIdentity(
            { kind: 'auth0', sub: auth0UserId },
            'profileUpdated'
          );
        });
    }
  }

  private loadPermissionsForIdentity(
    identity: AuthenticatedIdentity,
    source: string
  ) {
    const key = identityKey(identity);
    if (!key) return;
    if (
      this.lastIdentityKey === key &&
      this.permissionsChanged$.getValue() === PermissionsChangedEnum.Set
    ) {
      this.log.debug(
        `SecurityService: Permissions already loaded for ${key} (${source})`
      );
      return;
    }
    this.lastIdentityKey = key;
    this.log.debug(
      `SecurityService: Loading permissions for ${key} (${source})`
    );
    this.clearUserPermissions();

    // PERFORMANCE OPTIMIZATION: Try loading from cache first
    const cacheKey = `ups_security_${key}`;
    const cachedPermissions = this.loadFromCache(cacheKey);

    if (cachedPermissions) {
      this.log.debug('SecurityService: Using cached permissions');
      this.setUserPermissions(cachedPermissions);
      // Still refresh in background
      this.getSecurityForIdentity(identity).subscribe(
        (data: Array<unknown>) => {
          this.log.debug('SecurityService: Updated permissions from server');
          this.setUserPermissions(data);
          this.saveToCache(cacheKey, data);
        },
        (error) => {
          this.log.error(
            'SecurityService: Failed to refresh permissions',
            error
          );
        }
      );
    } else {
      // No cache, load from server
      this.getSecurityForIdentity(identity).subscribe(
        (data: Array<unknown>) => {
          this.log.debug('SecurityService: Permissions loaded successfully');
          this.setUserPermissions(data);
          this.saveToCache(cacheKey, data);
        },
        (error) => {
          this.log.error('SecurityService: Failed to load permissions', error);
        }
      );
    }
  }

  getPermissionStatus(
    securityConstant: string,
    permissionType: PermissionType
  ): Signal<boolean> {
    return computed(() => {
      const change = this.permissionChanged();

      if (change === PermissionsChangedEnum.Set) {
        const permissions = { ...this.getFeatureById(securityConstant) };
        return permissions[permissionType];
      } else if (
        permissionType === PermissionType.approvalNeeded ||
        permissionType === PermissionType.approvalNeededSelf
      ) {
        return true;
      }
      return false;
    });
  }

  getPermissionStatusObject(
    securityConstant: string
  ): Signal<SecurityFeatureModel> {
    return computed(() => {
      const change = this.permissionChanged();

      if (change === PermissionsChangedEnum.Set) {
        return this.getFeatureById(securityConstant);
      }

      return null;
    });
  }

  getRolesForApp(userId: string, appId: string) {
    const url = `api/roles/${encodeURIComponent(userId)}/${appId}`;

    return this.httpClient
      .get(url)
      .pipe(
        handleReturnObject(
          `Some error occurred while fetching roles for app: '${appId}' & user: '${userId}', original error message was: {{error}}`
        )
      );
  }

  // Finds UserId / HrRef for the signed-in user. A 404 is the expected
  // unresolved-caller signal (#29713), resolved to null not a red toast (Bug
  // 30363); other failures rethrow. Profile is optional at every call site.
  getSecurityProfileOfLoggedInUser(): Observable<SecurityProfile | null> {
    return this.httpClient
      .get<
        SecurityProfile | { data?: SecurityProfile; Data?: SecurityProfile }
      >('api/user/userProfile')
      .pipe(
        map(unwrapSecurityProfile),
        catchError((err: unknown) =>
          err instanceof HttpErrorResponse && err.status === 404
            ? of(null)
            : throwError(() => err)
        )
      );
  }

  /**
   * Latest published build for this app in the running build's own environment,
   * used to decide whether to prompt for a store update. The environment token must
   * be normalized first: the API answers an unknown one with a 500, not an empty
   * result, so an un-normalized lookup reads as an outage rather than a miss.
   */
  getAppInfo(appId: string) {
    const url = `api/MobileApplication/latest/${appId}/${normalizeEnvironmentName(
      environment.environment_name
    )}/${isIOS() ? 'ios' : 'android'}`;

    return this.httpClient
      .get<{
        mobileApplicationName: string;
        icon: string;
        buildNumber: string;
        versionNumber: string;
        environment: string;
        fileURL: string;
        bundleId: string;
        modifiedDate: string;
      }>(url)
      .pipe(
        handleReturnObject(
          `Some error occurred while fetching app info for app: '${appId}', original error message was: {{error}}`
        )
      );
  }

  /** Case sensitive search by name */
  public getFeatureGroupByName(
    featureGroupName: string
  ): SecurityFeatureGroupModel {
    if (featureGroupName in this.featureGroupsByName)
      return this.featureGroupsByName[featureGroupName];

    return new SecurityFeatureGroupModel();
  }

  /** search by guid */
  public getFeatureById(uniqId: string): SecurityFeatureModel {
    uniqId = (uniqId || '').toLowerCase();

    if (uniqId in this.featuresById) return this.featuresById[uniqId];

    return new SecurityFeatureModel();
  }

  /** Case sensitive search by uniq. name */
  public getFeatureByUniqName(uniqFeatureName: string): SecurityFeatureModel {
    if (uniqFeatureName in this.featuresByUniqName)
      return this.featuresByUniqName[uniqFeatureName];

    return new SecurityFeatureModel();
  }

  protected getSecurityForIdentity(
    identity: AuthenticatedIdentity
  ): Observable<unknown> {
    if (environment.security.securityOverride) {
      if (environment.security.securityOverrideJson) {
        this.log.debug('Loading OVERRIDDEN user permissions (local)');

        // NOTE: we're just fetching a plain JSON file
        return this.httpClient.get(environment.security.securityOverrideJson);
      } else {
        this.log.debug(
          'No OVERRIDDEN user permissions set, please update environment.ts: security.securityOverrideJson'
        );
      }
    }

    const appGuid = environment.security.appId;
    const url =
      identity.kind === 'auth0'
        ? `api/UserPermission/application/${encodeURIComponent(identity.sub)}/${appGuid}`
        : identity.kind === 'email'
          ? `api/UserPermission/application/by-email/${encodeURIComponent(identity.email)}/${appGuid}`
          : `api/UserPermission/application/byUserId/${encodeURIComponent(identity.id)}/${appGuid}`;

    return this.httpClient
      .get(url)
      .pipe(
        handleReturnObject(
          `Some error occurred while fetching permission for app: '${appGuid}' & user: '${identityKey(identity)}', original error message was: {{error}}`
        )
      );
  }

  /**
   * @deprecated use {@link getSecurityForIdentity}. Retained so consumers
   * that subclass SecurityService (there are a few) don't break.
   */
  protected getSecurityForUser(userAuth0Id: string): Observable<unknown> {
    return this.getSecurityForIdentity({ kind: 'auth0', sub: userAuth0Id });
  }

  /**
   * @deprecated
   * Avoid creating and accessing FeatureGroup via this.securityService.security[featureGroupId].
   * If you still need feature group, use: this.securityService.getFeatureGroupByName(name)
   *
   * For querying a given feature (globally) use:
   * - this.securityService.getFeatureById(id)
   * - or this.securityService.getfeatureByUniqName(name)
   *
   * NOTE: the get* functions won't return undefined | null, but they will return an empty object (with no permissions) as default!
   */
  get security(): ISecurityFeatureGroupsIndexer {
    return this.featureGroupsByName;
  }

  /** Clears cached permission state so the next sign-in reloads from the API. */
  resetSession(): void {
    this.lastIdentityKey = null;
    this.clearUserPermissions();
  }

  /**
   * Forces a permission reload for the signed-in user and completes when
   * permissions are applied (or the timeout elapses).
   */
  reloadPermissionsForCurrentUser(
    timeoutMs = 10_000,
    auth0Sub?: string | null
  ): Observable<boolean> {
    const identity = this.identityFromAccessToken(auth0Sub ?? undefined);
    if (identity) {
      return this.waitForPermissionsAfterReload(identity, timeoutMs);
    }

    return race(
      this.auth.getAccessTokenSilently().pipe(
        take(1),
        switchMap((tokenResult: string | { accessToken?: string }) => {
          const token =
            typeof tokenResult === 'string'
              ? tokenResult
              : tokenResult?.accessToken;
          const fromToken = this.identityFromAccessToken(token);
          if (!fromToken) {
            return of(false);
          }
          return this.waitForPermissionsAfterReload(fromToken, timeoutMs);
        })
      ),
      timer(timeoutMs).pipe(map(() => false))
    ).pipe(
      catchError((err) => {
        this.log.error(
          'SecurityService: reloadPermissionsForCurrentUser failed',
          err
        );
        return of(false);
      })
    );
  }

  private identityFromAccessToken(
    token?: string | null
  ): AuthenticatedIdentity | null {
    const sub = token
      ? (decodeJwt(token)?.sub as string | undefined)
      : undefined;
    return sub ? { kind: 'auth0', sub } : null;
  }

  private waitForPermissionsAfterReload(
    identity: AuthenticatedIdentity,
    timeoutMs: number
  ): Observable<boolean> {
    const waitForSet$ = this.permissionsChanged$.pipe(
      filter((p) => p === PermissionsChangedEnum.Set),
      take(1),
      map(() => true)
    );

    this.lastIdentityKey = null;
    this.loadPermissionsForIdentity(identity, 'reload');

    if (this.permissionsChanged$.getValue() === PermissionsChangedEnum.Set) {
      return of(true);
    }

    return race(waitForSet$, timer(timeoutMs).pipe(map(() => false)));
  }

  protected clearUserPermissions() {
    // this.log.debug('Clearing user permissions');
    this.featureGroupsByName = {};
    this.featuresById = {};
    this.featuresByUniqName = {};
    this.permissionsChanged$.next(PermissionsChangedEnum.Cleared);
  }

  protected setUserPermissions(data: Array<unknown>) {
    // this.log.debug('Setting user permissions');

    const typedData = data
      ? data.map((item) => new SecurityFeatureGroupModel(item))
      : null;

    // create:
    // groups by name
    // global features by id
    // global features by uniq. name
    this.featureGroupsByName = {};
    this.featuresById = {};
    this.featuresByUniqName = {};

    (typedData || []).forEach((featureGroup) => {
      this.featureGroupsByName[featureGroup.featureGroupName] = featureGroup;

      featureGroup.features.forEach((feature) => {
        this.featuresById[feature.featureId] = feature;
        this.featuresByUniqName[feature.uniqFeatureName] = feature;
      });
    });

    // event
    this.permissionsChanged$.next(PermissionsChangedEnum.Set);
  }

  /**
   * PERFORMANCE OPTIMIZATION: Cache security permissions in localStorage
   * This makes subsequent page loads nearly instant (no API call needed)
   */
  private saveToCache(key: string, data: Array<unknown>): void {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        version: '1.0',
      };
      this.storage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      this.log.warn('SecurityService: Failed to cache permissions', error);
    }
  }

  private loadFromCache(key: string): Array<unknown> | null {
    try {
      const cached = this.storage.getItem<string>(key);
      if (!cached) return null;

      const cacheData = JSON.parse(cached) as CachedPermissions;
      const cacheAge = Date.now() - cacheData.timestamp;
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours

      // Return cached data if it's less than 24 hours old
      if (cacheAge < maxAge && cacheData.version === '1.0') {
        return cacheData.data;
      }

      // Cache expired, remove it
      this.storage.removeItem(key);
      return null;
    } catch (error) {
      this.log.warn(
        'SecurityService: Failed to load cached permissions',
        error
      );
      return null;
    }
  }
}
